import type { TrackId } from '@src/audio/MusicTracks';

/**
 * Visual identity of a floor. Gameplay rules (Swarm, Fortified, ...) stay on
 * FloorModifiers; this is the place, the paint, and the broadcast name.
 *
 * Floor N uses THEMES[(N - 1) % THEMES.length], so a run reads as a tour:
 * Rome, Egypt, Aztec, then the older vaults, then it wraps.
 */
export interface FloorThemeDef {
  /** public/assets/arena/{asset}.png */
  readonly asset: string;
  /** Center stinger + minimap, broadcast voice, no emoji. */
  readonly title: string;
  /** Short tag beside the floor number. */
  readonly kicker: string;
  readonly wall: number;
  readonly trim: number;
  readonly surround: number;
  readonly fog: number;
  readonly fogOpacity: number;
  readonly torch: number;
  readonly rune: number;
  readonly gate: number;
  readonly portal: number;
  readonly track: Extract<TrackId, 'floor_obsidian' | 'floor_verdant' | 'floor_infernal'>;
  /** Procedural MusicEngine bed (0 obsidian / 1 verdant / 2 infernal). */
  readonly procBed: 0 | 1 | 2;
}

export const FLOOR_THEMES: readonly FloorThemeDef[] = [
  {
    asset: 'floor_rome',
    title: 'THE MARBLE COLOSSEUM',
    kicker: 'ROME',
    wall: 0x4a3028,
    trim: 0xd4a84a,
    surround: 0x140c0a,
    fog: 0xffe2a8,
    fogOpacity: 0.16,
    torch: 0xffc56a,
    rune: 0xe8c36a,
    gate: 0xf0dcb0,
    portal: 0xd4a84a,
    track: 'floor_verdant',
    procBed: 1,
  },
  {
    asset: 'floor_egyptian',
    title: 'THE SAND COURT',
    kicker: 'EGYPT',
    wall: 0x5a3c1c,
    trim: 0xe0b01a,
    surround: 0x1a1208,
    fog: 0xffc14a,
    fogOpacity: 0.18,
    torch: 0xffb020,
    rune: 0x3d8cff,
    gate: 0xf0d090,
    portal: 0xffc14a,
    track: 'floor_obsidian',
    procBed: 0,
  },
  {
    asset: 'floor_aztec',
    title: 'THE SUN PYRAMID',
    kicker: 'AZTEC',
    wall: 0x3a1c12,
    trim: 0x2ec27a,
    surround: 0x100806,
    fog: 0xff7a2a,
    fogOpacity: 0.17,
    torch: 0xff9a3a,
    rune: 0x3dff9a,
    gate: 0xe0a050,
    portal: 0x2ec27a,
    track: 'floor_verdant',
    procBed: 1,
  },
  {
    asset: 'floor_verdant',
    title: 'THE ROOT CATHEDRAL',
    kicker: 'VERDANT',
    wall: 0x1a261c,
    trim: 0x3a8a5a,
    surround: 0x05080a,
    fog: 0x5cff9a,
    fogOpacity: 0.14,
    torch: 0x7cff9a,
    rune: 0x5cff9a,
    gate: 0xc0d8c4,
    portal: 0x4dff88,
    track: 'floor_verdant',
    procBed: 1,
  },
  {
    asset: 'floor_obsidian',
    title: 'THE VOID VAULT',
    kicker: 'OBSIDIAN',
    wall: 0x1a1c26,
    trim: 0x6a3cff,
    surround: 0x05060a,
    fog: 0x9a5cff,
    fogOpacity: 0.16,
    torch: 0x9a5cff,
    rune: 0x9a5cff,
    gate: 0xc8b8ff,
    portal: 0xb07cff,
    track: 'floor_obsidian',
    procBed: 0,
  },
  {
    asset: 'floor_infernal',
    title: 'THE CINDER FORGE',
    kicker: 'INFERNAL',
    wall: 0x221410,
    trim: 0xc43a10,
    surround: 0x0a0404,
    fog: 0xff7a2a,
    fogOpacity: 0.2,
    torch: 0xff6a20,
    rune: 0xff7a2a,
    gate: 0xffb080,
    portal: 0xff7a2a,
    track: 'floor_infernal',
    procBed: 2,
  },
];

export function themeIndexForFloor(floor: number): number {
  return (Math.max(1, floor | 0) - 1) % FLOOR_THEMES.length;
}

export function floorThemeDef(floor: number): FloorThemeDef {
  return FLOOR_THEMES[themeIndexForFloor(floor)];
}

export function hexCss(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}
